import { useApi } from '@/@diffrag/hooks/useApi'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { api } from '@/api'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@/@diffrag/providers/user.provider'
import { AdminRole, type AdminJwtUser } from '@/constants/adminRole'
import { SERVICE_DOMAIN } from '@/constants/serviceDomain'

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: '활성',
  INACTIVE: '비활성',
}

function buildReferralUrls(code: string) {
  const trimmedCode = code.trim()
  return {
    home: `${SERVICE_DOMAIN}/en?ref=${encodeURIComponent(trimmedCode)}`,
    short: `${SERVICE_DOMAIN}/r/${encodeURIComponent(trimmedCode)}`,
    consultation: `${SERVICE_DOMAIN}/consultation?ref=${encodeURIComponent(trimmedCode)}`,
  }
}

function formatReferralUrlList(code: string | undefined) {
  if (!code) {
    return '-'
  }

  const urls = buildReferralUrls(code)
  return (
    <span className="text-12 text-gray-600">
      {urls.home}
      <br />
      {urls.short}
      <br />
      {urls.consultation}
    </span>
  )
}

export default function ReferralPartners() {
  const navigate = useNavigate()
  const user = useUser() as AdminJwtUser | undefined
  const isSuperAdmin = user?.role !== AdminRole.PARTNER_ADMIN

  const { data, isFetching } = useApi({
    id: 'referralPartners',
    fn: api().adminReferralPartner.adminReferralPartnerList,
    params: { page: 1 },
  })

  return (
    <ContentView>
      <div className="mb-16 rounded-8 border border-rose-200 bg-rose-50/50 p-16 text-14 text-gray-800">
        <p className="font-bold text-rose-900">URL 안내</p>
        <p className="mt-8 text-gray-600">
          {isSuperAdmin
            ? '코드 하나로 레퍼럴 URL이 발급됩니다. 상담 폼은 /consultation?ref=코드 형식으로 공유하세요.'
            : '관리자가 발급한 본인 레퍼럴 URL입니다. 행을 클릭하면 상세 URL을 확인할 수 있습니다.'}
        </p>
      </div>
      <SimpleTable
        isLoading={isFetching}
        createHref={isSuperAdmin ? '/referral-partners/edit' : undefined}
        options={{ disableNew: !isSuperAdmin, disableSort: true }}
        data={data?.items}
        onRowClick={(row) => navigate(`/referral-partners/edit?id=${row.id}`)}
        columns={[
          { key: 'id', label: 'ID', isPrimary: true, width: 60 },
          { key: 'code', label: '코드' },
          ...(isSuperAdmin
            ? [{ key: 'ownerAdminName' as const, label: '소유 관리자' }]
            : []),
          {
            key: 'status',
            label: '상태',
            valueMap: STATUS_LABELS,
          },
          {
            key: 'slug',
            label: '레퍼럴 URL',
            formatter: (slug) => formatReferralUrlList(String(slug ?? '')),
          },
        ]}
      />
    </ContentView>
  )
}
